"use client";

import { c as _c } from "react/compiler-runtime";
import dynamic from "next/dynamic";
import type { Locale } from "@/i18n-config";
import GlobalLoginModalWrapper from "./GlobalLoginModalWrapper";
import { Toaster } from "@/components/ui/sonner";
// The new-collection dialog is fetched the first time it is opened, then stays mounted — closing
// it postpones a funding round, which must survive a reopen exactly as before.
const CreateCollectionDialog = dynamic(() => import("@/features/collections/create/v2/CreateCollectionDialog"), {
  ssr: false
});
import RouteLoader from "@/components/providers/RouteLoader";
import CropMount from "@/features/home-v2/crop/CropMount";
import MaintenanceNotice from "@/features/home-v2/notice/MaintenanceNotice";
import OfflineNavigationGuard from "./OfflineNavigationGuard";
// The socket, its watchers and the process dock exist only for a signed-in
// visitor, so they are one chunk loaded once there is a token — a guest page
// (and the crawler) never downloads or hydrates them. See SignedInWatchers.
const SignedInWatchers = dynamic(() => import("./SignedInWatchers"), {
  ssr: false
});
import NavProgress from "@/features/home-v2/NavProgress";
import RouteScrollTop from "@/features/home-v2/RouteScrollTop";
import PWAController from "@/components/pwa/PWAController";
import AppShellProbe from "./AppShellProbe";
// The launch sequence (splash + first-run intro). Mounted in BOTH shell
// branches for the reason AppShellProbe is: a cold launch into /auth or
// /connect-wallet is still a launch, and the fullscreen takeover would
// otherwise be the first thing an installed app ever shows.
import LaunchExperience from "@/features/onboarding/LaunchExperience";
import { useEffect, useState } from "react";
import { useAuthStore } from "@/store/useAuthStore";
import { useFreshSessionUser } from "@/features/auth/useFreshSessionUser";
import HomeV2Shell from "@/features/home-v2/HomeV2Shell";
import type { Currency } from "@/features/currencies/types";
import { useCreateCollectionModalStore } from "@/store/useCreateCollectionModalStore";
import { usePathname, useSearchParams } from "next/navigation";
import Cookies from "js-cookie";
import type { Dictionary } from "@/dictionaries/types";
import { resolveShellMode } from "@/lib/navigation";
import { capIosFocusZoom, isIos } from "@/lib/touch-focus";
type ClientLayoutProps = {
  children: React.ReactNode;
  lang: Locale;
  /** For the chrome's rate ticker — fetched once in the locale layout. */
  currencies?: Currency[];
  dict: Dictionary;
};
export default function ClientLayout(t0) {
  const $ = _c(94);
  const {
    children,
    lang,
    dict,
    currencies: t1
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const currencies = t2;
  const {
    user,
    logout,
    token
  } = useAuthStore();
  useFreshSessionUser();
  const isCreateCollectionModalOpen = useCreateCollectionModalStore(_temp);
  const [collectionDialogUsed, setCollectionDialogUsed] = useState(false);
  let t3;
  let t4;
  if ($[2] !== isCreateCollectionModalOpen) {
    t3 = () => {
      if (isCreateCollectionModalOpen) {
        setCollectionDialogUsed(true);
      }
    };
    t4 = [isCreateCollectionModalOpen];
    $[2] = isCreateCollectionModalOpen;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  const setCreateCollectionModalOpen = useCreateCollectionModalStore(_temp2);
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const shellMode = resolveShellMode(pathname);
  const isFullscreenRoute = shellMode === "fullscreen";
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  useEffect(_temp4, t5);
  let t6;
  if ($[6] !== pathname) {
    t6 = [pathname];
    $[6] = pathname;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  useEffect(_temp5, t6);
  let t7;
  let t8;
  if ($[8] !== logout || $[9] !== token || $[10] !== user) {
    t7 = () => {
      const cookieToken = Cookies.get("token");
      if ((user || token) && !cookieToken) {
        logout();
      }
    };
    t8 = [user, token, logout];
    $[8] = logout;
    $[9] = token;
    $[10] = user;
    $[11] = t7;
    $[12] = t8;
  } else {
    t7 = $[11];
    t8 = $[12];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[13] !== logout || $[14] !== searchParams) {
    t9 = () => {
      if (searchParams?.get("session_expired") === "true") {
        console.log("Session expired detected in ClientLayout");
        logout();
        const url = new URL(window.location.href);
        url.searchParams.delete("session_expired");
        window.history.replaceState({}, "", url);
      }
    };
    t10 = [searchParams, logout];
    $[13] = logout;
    $[14] = searchParams;
    $[15] = t10;
    $[16] = t9;
  } else {
    t10 = $[15];
    t9 = $[16];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[17] !== logout) {
    t11 = () => {
      const handleStorageChange = e => {
        if (e.key === "auth-storage" && e.newValue) {
          const parsed = JSON.parse(e.newValue);
          if (!parsed.state?.token) {
            logout();
          } else {
            useAuthStore.persist.rehydrate();
          }
        }
      };
      window.addEventListener("storage", handleStorageChange);
      return () => window.removeEventListener("storage", handleStorageChange);
    };
    t12 = [logout];
    $[17] = logout;
    $[18] = t11;
    $[19] = t12;
  } else {
    t11 = $[18];
    t12 = $[19];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[20] !== lang) {
    t13 = () => {
      document.documentElement.lang = lang;
      document.documentElement.dir = lang === "fa" ? "rtl" : "ltr";
      document.body.setAttribute("data-app-lang", lang);
    };
    t14 = [lang];
    $[20] = lang;
    $[21] = t13;
    $[22] = t14;
  } else {
    t13 = $[21];
    t14 = $[22];
  }
  useEffect(t13, t14);
  if (isFullscreenRoute) {
    let t15;
    if ($[23] !== dict.states.loading) {
      t15 = <RouteLoader label={dict.states.loading} />;
      $[23] = dict.states.loading;
      $[24] = t15;
    } else {
      t15 = $[24];
    }
    let t16;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = <AppShellProbe />;
      $[25] = t16;
    } else {
      t16 = $[25];
    }
    let t17;
    if ($[26] !== dict.onboarding || $[27] !== lang) {
      t17 = <LaunchExperience lang={lang} copy={dict.onboarding} />;
      $[26] = dict.onboarding;
      $[27] = lang;
      $[28] = t17;
    } else {
      t17 = $[28];
    }
    let t18;
    if ($[29] !== lang) {
      t18 = <OfflineNavigationGuard lang={lang} />;
      $[29] = lang;
      $[30] = t18;
    } else {
      t18 = $[30];
    }
    let t19;
    if ($[31] !== lang || $[32] !== token) {
      t19 = token ? <SignedInWatchers lang={lang} /> : null;
      $[31] = lang;
      $[32] = token;
      $[33] = t19;
    } else {
      t19 = $[33];
    }
    let t20;
    let t21;
    if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = <NavProgress />;
      t21 = <RouteScrollTop />;
      $[34] = t20;
      $[35] = t21;
    } else {
      t20 = $[34];
      t21 = $[35];
    }
    const t22 = lang === "fa" ? "fa" : "en";
    let t23;
    if ($[36] !== t22) {
      t23 = <MaintenanceNotice lang={t22} />;
      $[36] = t22;
      $[37] = t23;
    } else {
      t23 = $[37];
    }
    const t24 = lang === "fa" ? "rtl" : "ltr";
    let t25;
    if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
      t25 = {
        fontFamily: "var(--app-font-sans)"
      };
      $[38] = t25;
    } else {
      t25 = $[38];
    }
    let t26;
    if ($[39] !== t24) {
      t26 = <Toaster richColors={false} position="top-center" dir={t24} style={t25} />;
      $[39] = t24;
      $[40] = t26;
    } else {
      t26 = $[40];
    }
    let t27;
    if ($[41] !== children || $[42] !== t15 || $[43] !== t17 || $[44] !== t18 || $[45] !== t19 || $[46] !== t23 || $[47] !== t26) {
      t27 = <>{t15}{t16}{t17}{t18}{t19}{t20}{t21}{t23}{children}{t26}</>;
      $[41] = children;
      $[42] = t15;
      $[43] = t17;
      $[44] = t18;
      $[45] = t19;
      $[46] = t23;
      $[47] = t26;
      $[48] = t27;
    } else {
      t27 = $[48];
    }
    return t27;
  }
  let t15;
  if ($[49] !== dict.states.loading) {
    t15 = <RouteLoader label={dict.states.loading} />;
    $[49] = dict.states.loading;
    $[50] = t15;
  } else {
    t15 = $[50];
  }
  let t16;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <AppShellProbe />;
    $[51] = t16;
  } else {
    t16 = $[51];
  }
  let t17;
  if ($[52] !== dict.onboarding || $[53] !== lang) {
    t17 = <LaunchExperience lang={lang} copy={dict.onboarding} />;
    $[52] = dict.onboarding;
    $[53] = lang;
    $[54] = t17;
  } else {
    t17 = $[54];
  }
  let t18;
  let t19;
  if ($[55] !== lang) {
    t18 = <PWAController lang={lang} />;
    t19 = <OfflineNavigationGuard lang={lang} />;
    $[55] = lang;
    $[56] = t18;
    $[57] = t19;
  } else {
    t18 = $[56];
    t19 = $[57];
  }
  let t20;
  if ($[58] !== lang || $[59] !== token) {
    t20 = token ? <SignedInWatchers lang={lang} /> : null;
    $[58] = lang;
    $[59] = token;
    $[60] = t20;
  } else {
    t20 = $[60];
  }
  let t21;
  let t22;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <NavProgress />;
    t22 = <RouteScrollTop />;
    $[61] = t21;
    $[62] = t22;
  } else {
    t21 = $[61];
    t22 = $[62];
  }
  let t23;
  if ($[63] !== lang) {
    t23 = <GlobalLoginModalWrapper lang={lang} />;
    $[63] = lang;
    $[64] = t23;
  } else {
    t23 = $[64];
  }
  const t24 = lang === "fa" ? "fa" : "en";
  let t25;
  if ($[65] !== t24) {
    t25 = <MaintenanceNotice lang={t24} />;
    $[65] = t24;
    $[66] = t25;
  } else {
    t25 = $[66];
  }
  let t26;
  if ($[67] !== children || $[68] !== currencies || $[69] !== dict || $[70] !== lang || $[71] !== t25) {
    t26 = <HomeV2Shell lang={lang} currencies={currencies} dict={dict} notice={t25}>{children}</HomeV2Shell>;
    $[67] = children;
    $[68] = currencies;
    $[69] = dict;
    $[70] = lang;
    $[71] = t25;
    $[72] = t26;
  } else {
    t26 = $[72];
  }
  let t27;
  if ($[73] !== collectionDialogUsed || $[74] !== isCreateCollectionModalOpen || $[75] !== lang || $[76] !== setCreateCollectionModalOpen) {
    t27 = collectionDialogUsed || isCreateCollectionModalOpen ? <CreateCollectionDialog lang={lang} open={isCreateCollectionModalOpen} onOpenChange={setCreateCollectionModalOpen} /> : null;
    $[73] = collectionDialogUsed;
    $[74] = isCreateCollectionModalOpen;
    $[75] = lang;
    $[76] = setCreateCollectionModalOpen;
    $[77] = t27;
  } else {
    t27 = $[77];
  }
  const t28 = lang === "fa" ? "fa" : "en";
  let t29;
  if ($[78] !== t28) {
    t29 = <CropMount lang={t28} />;
    $[78] = t28;
    $[79] = t29;
  } else {
    t29 = $[79];
  }
  const t30 = lang === "fa" ? "rtl" : "ltr";
  let t31;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = {
      fontFamily: "var(--app-font-sans)"
    };
    $[80] = t31;
  } else {
    t31 = $[80];
  }
  let t32;
  if ($[81] !== t30) {
    t32 = <Toaster richColors={false} position="top-center" dir={t30} style={t31} />;
    $[81] = t30;
    $[82] = t32;
  } else {
    t32 = $[82];
  }
  let t33;
  if ($[83] !== t15 || $[84] !== t17 || $[85] !== t18 || $[86] !== t19 || $[87] !== t20 || $[88] !== t23 || $[89] !== t26 || $[90] !== t27 || $[91] !== t29 || $[92] !== t32) {
    t33 = <>{t15}{t16}{t17}{t18}{t19}{t20}{t21}{t22}{t23}{t26}{t27}{t29}{t32}</>;
    $[83] = t15;
    $[84] = t17;
    $[85] = t18;
    $[86] = t19;
    $[87] = t20;
    $[88] = t23;
    $[89] = t26;
    $[90] = t27;
    $[91] = t29;
    $[92] = t32;
    $[93] = t33;
  } else {
    t33 = $[93];
  }
  return t33;
}
function _temp5() {
  if (isIos()) {
    capIosFocusZoom();
  }
}
function _temp4() {
  Promise.resolve(useAuthStore.persist.rehydrate()).then(_temp3);
}
function _temp3() {
  if (!useAuthStore.getState().token && Cookies.get("token")) {
    Cookies.remove("token", {
      path: "/"
    });
  }
}
function _temp2(state_0) {
  return state_0.setOpen;
}
function _temp(state) {
  return state.isOpen;
}